import { Children, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import styles from './Carousel.module.css'

export interface CarouselProps {
  /** One slide per child. */
  children: ReactNode
  /** Names the carousel for screen readers, e.g. "Featured competitions". */
  label: string
  /** Left of the controls — usually a small section label. */
  header?: ReactNode
  /** Controlled slide index. Omit to let the carousel keep its own. */
  index?: number
  onIndexChange?: (index: number) => void
  className?: string
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/**
 * Slides side by side in a scroll-snapping track: a trackpad's sideways
 * scroll, a touch swipe or a wheel slides it natively — momentum and all —
 * and it settles on a whole slide. Arrows, dots and the arrow keys scroll the
 * same track, and the counter follows wherever it lands.
 *
 * It never advances on its own: a slide that moves while someone is reading
 * it — or reaching for its button — is the thing people hate about carousels.
 * Slides out of view are hidden from screen readers and from Tab. With a
 * single slide it is just that slide, with no controls.
 */
export function Carousel({ children, label, header, index, onIndexChange, className }: CarouselProps) {
  const slides = Children.toArray(children)
  const count = slides.length
  const [own, setOwn] = useState(0)
  const current = Math.min(Math.max(index ?? own, 0), Math.max(count - 1, 0))
  const track = useRef<HTMLDivElement>(null)

  const select = useCallback((i: number) => {
    if (index == null) setOwn(i)
    onIndexChange?.(i)
  }, [index, onIndexChange])

  const scrollToSlide = useCallback((i: number) => {
    // A controlled index past the last slide has nowhere to scroll to.
    const slide = track.current!.children[i] as HTMLElement | undefined
    if (!slide) return
    track.current!.scrollTo({ left: slide.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }, [])

  const go = (next: number) => {
    // Wraps, so "next" on the last slide comes back to the first.
    const wrapped = ((next % count) + count) % count
    select(wrapped)
    scrollToSlide(wrapped)
  }

  // A controlled index moved by the parent: bring that slide into view.
  useEffect(() => {
    if (index != null) scrollToSlide(index)
  }, [index, scrollToSlide])

  // The track was scrolled directly (trackpad, swipe, wheel): the slide
  // nearest its left edge is the current one.
  const onScroll = () => {
    const el = track.current!
    let nearest = 0
    let best = Infinity
    Array.from(el.children).forEach((child, i) => {
      const d = Math.abs((child as HTMLElement).offsetLeft - el.scrollLeft)
      if (d < best) { best = d; nearest = i }
    })
    if (nearest !== current) select(nearest)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (count < 2) return
    if (e.key === 'ArrowRight') { e.preventDefault(); go(current + 1) }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(current - 1) }
  }

  if (count === 0) return null
  const paged = count > 1

  return (
    <section
      className={[styles.carousel, className].filter(Boolean).join(' ')}
      aria-roledescription="carousel"
      aria-label={label}
      onKeyDown={onKeyDown}
    >
      {(header || paged) && (
        <div className={styles.bar}>
          <div className={styles.header}>{header}</div>
          {paged && (
            <div className={styles.controls}>
              <button type="button" className={styles.arrow} onClick={() => go(current - 1)} aria-label="Previous">
                <ChevronLeft size={18} />
              </button>
              <span className={styles.position} aria-live="polite">{current + 1} / {count}</span>
              <button type="button" className={styles.arrow} onClick={() => go(current + 1)} aria-label="Next">
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      <div ref={track} className={styles.track} onScroll={paged ? onScroll : undefined}>
        {slides.map((slide, i) => (
          <div
            key={i}
            className={styles.slide}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            aria-hidden={i === current ? undefined : true}
            inert={i === current ? undefined : true}
          >
            {slide}
          </div>
        ))}
      </div>

      {paged && (
        <div className={styles.dots}>
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`${styles.dot} ${i === current ? styles.dotActive : ''}`}
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === current ? 'true' : undefined}
            />
          ))}
        </div>
      )}
    </section>
  )
}
